import { is_set_object } from '$lib/js/helpers'
import type { Page } from '@sveltejs/kit'
import { gt } from 'svelte-i18n-lingui'

/**
 * Return page title.
 */
export function getPageTitle(page_info: Page): string {
  const pageName = (page_info.route.id || '').substring(1)

  const meta_title =
    page_info.data.page_data?.meta_title ||
    page_info.data.page_info?.meta_title ||
    page_info.data.homepage?.meta_title ||
    page_info.data.help_center?.meta_title

  if (meta_title) {
    return meta_title
  }

  let path = page_info.url.pathname

  if (path.includes('blog/c/')) {
    return gt`Bixoto.com - ${page_info.params.cat_name} - Blog`
  }
  if (path.includes('blog/')) {
    return gt`${page_info.data.page_data?.page_title} - Bixoto.com`
  }
  if (pageName.includes('/jobs/[job_url]')) {
    return gt`Job offer - ${page_info.data.page_data?.title} - Bixoto.com`
  }
  if (path.includes('jobs/')) {
    return gt`Job offers in Bixoto.com`
  }
  if (path.includes('/p/')) {
    if (is_set_object(page_info.data.product)) {
      return gt`${page_info.data.product.name} - Bixoto.com`
    }
  }
  if (path.includes('/b/all')) {
    if (is_set_object(page_info.data.category)) {
      return gt`All our brands in ${page_info.data.category.name} - Bixoto.com`
    }
  }
  if (path.includes('/b/')) {
    if (is_set_object(page_info.data.category) && is_set_object(page_info.data.brand)) {
      return (
        gt`${page_info.data.category.name}: the best products of ${page_info.data.brand.page_title} - Bixoto.com`
      )
    }
  }
  if (path.includes('/c/')) {
    if (page_info.data.universe) {
      return page_info.data.universe?.title
    }
    if (is_set_object(page_info.data.category)) {
      return gt`Buy ${page_info.data.category.name} - Bixoto.com`
    }
  }
	if (path.includes('/t/')) {
		if (is_set_object(page_info.data.category)) {
			return gt`${page_info.data.category.name} - ${page_info.data.tag}s - Bixoto.com`
		}
		if (page_info.data.tag) {
			return gt`${page_info.data.tag}s - Bixoto.com`
		}
	}
  if (path.includes('/u/')) {
    if (page_info.data.universe) {
      return page_info.data.universe?.meta_title
    }
  }
  if (path.includes('/catalogsearch/') && is_set_object(page_info.url.searchParams.get('q'))) {
    return gt`Buy ${<string>page_info.url.searchParams.get('q')} - Bixoto.com`
  }
  if (path.includes('/brands/') && page_info.data.brand) {
    return `${page_info.data.brand?.page_title} - Bixoto.com`
  }
  if (path.endsWith('/brands')) {
    return gt`Our brands - Bixoto.com`
  }
  if (pageName.includes('/opiniones')) {
    return gt`Reviews and ratings of clients - Bixoto.com`
  }

  // Help center SEO
  // Postventa
  if (path.includes('/centro-de-ayuda/postventa/devoluciones')) {
    return gt`Return (withdrawal) of order - Bixoto.com`
  }
  if (path.includes('/centro-de-ayuda/postventa/incidencias')) {
    return gt`Manage issues with my order - Bixoto.com`
  }
  if (path.includes('/centro-de-ayuda/postventa/solicitar-factura')) {
    return gt`Request bill of my order - Bixoto.com`
  }
  if (path.includes('/centro-de-ayuda/postventa')) {
    return gt`Manage queries and incidents with your products - Bixoto.com`
  }
  // Seguimiento
  if (path.includes('/centro-de-ayuda/seguimiento/cancelacion-pedido')) {
    return gt`Order cancellation - Bixoto.com`
  }
  if (path.includes('/centro-de-ayuda/seguimiento/estado-pedido')) {
    return gt`Order tracking - Bixoto.com`
  }
  if (path.includes('/centro-de-ayuda/seguimiento/corregir-datos')) {
    return gt`Correct my order data - Bixoto.com`
  }
  if (path.includes('/centro-de-ayuda/seguimiento')) {
    return gt`Questions and actions on an order - Bixoto.com`
  }
  // Preventa
  if (path.includes('/centro-de-ayuda/preventa/consultas-producto')) {
    return gt`Questions about a product - Bixoto.com`
  }
  if (path.includes('/centro-de-ayuda/preventa/informacion-de-compra')) {
    return gt`Purchase information (pre-sale) - Bixoto.com`
  }
  if (path.includes('/centro-de-ayuda/preventa/informacion-generica')) {
    return gt`General information (pre-sale) - Bixoto.com`
  }
  if (path.includes('/centro-de-ayuda/preventa')) {
    return gt`Pre-sale - Help Center - Bixoto.com`
  }
  // Generales
  if (path.includes('/centro-de-ayuda/otras-cuestiones')) {
    return gt`Contact us - Bixoto.com`
  }
  if (path.includes('/centro-de-ayuda')) {
    return gt`Help, support and contact center - Bixoto.com`
  }

  return gt`Bixoto your sustainable, conscious and five-star store`
}

/**
 * Get page meta description.
 */
export function getMetaDescription(page_info: Page): string {
  let pageName = (page_info.route.id || '').substring(1)
  let meta_description =
    page_info.data.page_data?.meta_description ||
    page_info.data.page_info?.meta_description ||
    page_info.data.homepage?.meta_description ||
    page_info.data.help_center?.meta_description

  if (meta_description) {
    return meta_description
  }

  let path = page_info.url.pathname

  if (pageName.includes('/blog/[post_url]')) {
    return page_info.data.page_data?.post_excerpt
  }
  if (path.includes('blog/c/')) {
    return page_info.params.cat_name
  }
  if (pageName.includes('/jobs/[job_url]')) {
    return page_info.data.page_data?.description
  }
  if (path.includes('jobs/')) {
    return gt`Do you want to be part of the Bixoto team? Check out all our job offers and don't hesitate to contact us.`
  }
  if (path.includes('/p/') && is_set_object(page_info.data.product)) {
    return (
      gt`Buy ${page_info.data.product.name} at the best price and we will send it to you wherever you want. CO2-neutral shipping within 24/48 hours!`
    )
  }
  if (path.includes('/b/all') && is_set_object(page_info.data.category)) {
    return (
      gt`Check out all our brands at ${page_info.data.category.name}. Exclusive promotions and products. Visit us and take advantage of our CO2-neutral 24/48h shipping.`
    )
  }
  if (
    path.includes('/b/') &&
    is_set_object(page_info.data.category) &&
    is_set_object(page_info.data.brand)
  ) {
    return (
      gt`The best selection of ${page_info.data.brand?.page_title} in ${page_info.data.category.name}. Exclusive promotions and products. Visit us and take advantage of our CO2-neutral 24/48h shipping.`
    )
  }
  if (path.includes('/c/')) {
    if (page_info.data.universe) {
      return page_info.data.universe?.description
    }
    if (is_set_object(page_info.data.category)) {
      return gt`The best selection in ${page_info.data.category.name}. Exclusive promotions and products. Visit us and take advantage of our CO2-neutral 24/48h shipping.`
    }
  }
	if (path.includes('/t/')) {
		if (is_set_object(page_info.data.category)) {
			return gt`Here you will find all ${page_info.data.tag} products for ${page_info.data.category.name} - Sustainable and affordable at Bixoto.com`
		}
		if (page_info.data.tag) {
			return gt`Here you will find all ${page_info.data.tag} products - Sustainable and affordable at Bixoto.com`
		}
	}
  if (path.includes('/u/')) {
    if (page_info.data.universe) {
      return page_info.data.universe?.meta_description
    }
  }
  if (path.includes('/brands') && page_info.data.brand) {
    return gt`The best selection of ${page_info.data.brand?.page_title} products. Visit us and discover the latest news from ${page_info.data.brand?.page_title} at the best price.`
  }
  if (path.includes('/catalogsearch/') && is_set_object(page_info.url.searchParams.get('q'))) {
    return gt`Exclusive promotions on ${<string>page_info.url.searchParams.get('q')}. Discover the latest trends. Visit us and take advantage of our CO2-neutral 24/48h shipping.`
  }
  if (pageName.includes('/opiniones')) {
    return gt`All our customers' opinions and ratings on Google, Trustpilot and Reviews.io. Check out their reviews of Bixoto and leave yours too!`
  }

  // Help center SEO
  // Postventa
  if (path.includes('/centro-de-ayuda/postventa/devoluciones')) {
    return gt`Please note that you have 14 calendar days from the date of delivery of the order to cancel and return any product, and that the products must be in perfect condition.`
  }
  if (path.includes('/centro-de-ayuda/postventa/incidencias')) {
    return gt`Manage incidents with your order here: missing products, damaged products, malfunctions, wrong products, etc.`
  }
  if (path.includes('/centro-de-ayuda/postventa/solicitar-factura')) {
    return gt`Request an invoice for your order here. To download an invoice, you must be logged into your Bixoto account. If you do not have an account yet, you can create one now.`
  }
  if (path.includes('/centro-de-ayuda/postventa')) {
    return gt`Resolve your queries and issues with your order here. Request your invoice, manage incidents or make a return.`
  }
  // Seguimiento
  if (path.includes('/centro-de-ayuda/seguimiento/cancelacion-pedido')) {
    return gt`Cancel your order here if it has not yet been shipped. Please note that orders that have already been shipped cannot be cancelled.`
  }
  if (path.includes('/centro-de-ayuda/seguimiento/estado-pedido')) {
    return gt`Access your order tracking page here. On this page you can check the status of your order, track the shipment and manage incidents.`
  }
  if (path.includes('/centro-de-ayuda/seguimiento/corregir-datos')) {
    return gt`If you have entered incorrect data or your order is missing information (shipping address, email, etc.), please contact us here.`
  }
  if (path.includes('/centro-de-ayuda/seguimiento')) {
    return gt`Solve your queries or issues regarding the status of your order here. Correct order details or check its status.`
  }
  // Preventa
  if (path.includes('/centro-de-ayuda/preventa/consultas-producto')) {
    return gt`Contact us by email or WhatsApp and a member of our team will contact you to answer your questions.`
  }
  if (path.includes('/centro-de-ayuda/preventa/informacion-de-compra')) {
    return gt`All the information about purchasing at Bixoto. How to buy, shipping costs, returns, etc.`
  }
  if (path.includes('/centro-de-ayuda/preventa/informacion-generica')) {
    return gt`Find all the information about Bixoto here. Who we are, our mission, vision and values, and our commitment to the environment.`
  }
  if (path.includes('/centro-de-ayuda/preventa')) {
    return gt`Find all the information you need before purchasing from Bixoto here. Learn about the company, purchase conditions or ask a question about a product.`
  }
  // Generales
  if (path.includes('/centro-de-ayuda/otras-cuestiones')) {
    return gt`If you can't find the information you need, or you don't know how to resolve your problem, contact us here.`
  }
  if (path.includes('/centro-de-ayuda')) {
    return gt`In the bixoto help center you can resolve your queries and incidents quickly and efficiently. Contact our support team here in an agile manner.`
  }

  return gt`More than 500,000 products at your fingertips. Buy all cosmetics, home, kitchen, garden, computer and many more products online at the best price.`
}
